import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  User, Mail, Phone, Building, Shield, Bell, Lock,
  Save, Eye, EyeOff, CheckCircle, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    organization_name: '',
    specialty: '',
    npi_number: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [notifications, setNotifications] = useState({
    email_notifications: true,
    sms_notifications: false,
    push_notifications: true
  });

  // Use useMemo to sync profile with user data once
  const userEmail = user?.email;
  useEffect(() => {
    if (user && userEmail) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        organization_name: user.organization_name || '',
        specialty: user.specialty || '',
        npi_number: user.npi_number || ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('hospice_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const handleProfileUpdate = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(profile)
      });

      if (response.ok) {
        toast.success('Profile updated successfully');
        if (refreshUser) refreshUser();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update profile');
      }
    } catch (error) {
      toast.error('Error updating profile');
    }
    setLoading(false);
  };

  const handlePasswordChange = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });

      if (response.ok) {
        toast.success('Password changed successfully');
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to change password');
      }
    } catch (error) {
      toast.error('Error changing password');
    }
    setLoading(false);
  };

  const handleNotificationUpdate = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/notifications/preferences`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(notifications)
      });

      if (response.ok) {
        toast.success('Notification preferences updated');
      } else {
        toast.error('Failed to update preferences');
      }
    } catch (error) {
      toast.error('Error updating preferences');
    }
    setLoading(false);
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-700',
      administrator: 'bg-red-100 text-red-700',
      physician: 'bg-blue-100 text-blue-700',
      nurse: 'bg-green-100 text-green-700',
      patient: 'bg-purple-100 text-purple-700',
      facility: 'bg-amber-100 text-amber-700',
      staff: 'bg-teal-100 text-teal-700'
    };
    return colors[role?.toLowerCase()] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="profile-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <User className="h-7 w-7 text-teal-600" />
            {t('profile.title', 'My Profile')}
          </h1>
          <p className="text-gray-600 mt-1">{t('profile.subtitle', 'Manage your account settings and preferences')}</p>
        </div>

        {/* Profile Summary Card */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div>
                <h2 className="text-xl font-bold">{user?.name}</h2>
                <p className="text-gray-500">{user?.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={getRoleBadgeColor(user?.role)}>
                    {user?.role}
                  </Badge>
                  {user?.approved ? (
                    <Badge variant="outline" className="text-green-600 border-green-200">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      {t('profile.verified', 'Verified')}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-amber-600 border-amber-200">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      {t('profile.pendingApproval', 'Pending Approval')}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 w-full max-w-md mb-6">
            <TabsTrigger value="profile">{t('profile.profile', 'Profile')}</TabsTrigger>
            <TabsTrigger value="security">{t('profile.security', 'Security')}</TabsTrigger>
            <TabsTrigger value="notifications">{t('profile.notifications', 'Notifications')}</TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>{t('profile.profileInfo', 'Profile Information')}</CardTitle>
                <CardDescription>{t('profile.updatePersonal', 'Update your personal information')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>{t('profile.fullName', 'Full Name')}</Label>
                    <Input
                      value={profile.name}
                      onChange={(e) => setProfile({...profile, name: e.target.value})}
                      data-testid="profile-name"
                    />
                  </div>
                  <div>
                    <Label>{t('profile.email', 'Email')}</Label>
                    <Input
                      type="email"
                      value={profile.email}
                      disabled
                      className="bg-gray-50"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>{t('profile.phone', 'Phone')}</Label>
                    <Input
                      value={profile.phone}
                      onChange={(e) => setProfile({...profile, phone: e.target.value})}
                      placeholder="555-123-4567"
                    />
                  </div>
                  <div>
                    <Label>{t('profile.organization', 'Organization')}</Label>
                    <Input
                      value={profile.organization_name}
                      onChange={(e) => setProfile({...profile, organization_name: e.target.value})}
                      placeholder={t('profile.orgPlaceholder', 'Organization name')}
                    />
                  </div>
                </div>
                {(user?.role === 'physician' || user?.role === 'nurse') && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t('profile.specialty', 'Specialty')}</Label>
                      <Input
                        value={profile.specialty}
                        onChange={(e) => setProfile({...profile, specialty: e.target.value})}
                        placeholder={t('profile.specialtyPlaceholder', 'e.g., Internal Medicine')}
                      />
                    </div>
                    <div>
                      <Label>{t('profile.npiNumber', 'NPI Number')}</Label>
                      <Input
                        value={profile.npi_number}
                        onChange={(e) => setProfile({...profile, npi_number: e.target.value})}
                        placeholder={t('profile.npiPlaceholder', '10-digit NPI')}
                      />
                    </div>
                  </div>
                )}
                <div className="flex justify-end pt-4">
                  <Button onClick={handleProfileUpdate} disabled={loading}>
                    <Save className="h-4 w-4 mr-2" />
                    {t('profile.saveChanges', 'Save Changes')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  {t('profile.changePassword', 'Change Password')}
                </CardTitle>
                <CardDescription>{t('profile.passwordDescription', 'Update your password to keep your account secure')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{t('profile.currentPassword', 'Current Password')}</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                      data-testid="current-password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>{t('profile.newPassword', 'New Password')}</Label>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                    data-testid="new-password"
                  />
                </div>
                <div>
                  <Label>{t('profile.confirmPassword', 'Confirm New Password')}</Label>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                    data-testid="confirm-password"
                  />
                </div>
                <div className="flex justify-end pt-4">
                  <Button onClick={handlePasswordChange} disabled={loading}>
                    <Shield className="h-4 w-4 mr-2" />
                    {t('profile.updatePassword', 'Update Password')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  {t('profile.notificationPrefs', 'Notification Preferences')}
                </CardTitle>
                <CardDescription>{t('profile.notificationDescription', 'Manage how you receive notifications')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base">{t('profile.emailNotifications', 'Email Notifications')}</Label>
                    <p className="text-sm text-gray-500">{t('profile.emailNotificationsDesc', 'Receive updates via email')}</p>
                  </div>
                  <Switch
                    checked={notifications.email_notifications}
                    onCheckedChange={(v) => setNotifications({...notifications, email_notifications: v})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base">{t('profile.smsNotifications', 'SMS Notifications')}</Label>
                    <p className="text-sm text-gray-500">{t('profile.smsNotificationsDesc', 'Receive text messages for urgent updates')}</p>
                  </div>
                  <Switch
                    checked={notifications.sms_notifications}
                    onCheckedChange={(v) => setNotifications({...notifications, sms_notifications: v})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base">{t('profile.pushNotifications', 'Push Notifications')}</Label>
                    <p className="text-sm text-gray-500">{t('profile.pushNotificationsDesc', 'Receive in-app notifications')}</p>
                  </div>
                  <Switch
                    checked={notifications.push_notifications}
                    onCheckedChange={(v) => setNotifications({...notifications, push_notifications: v})}
                  />
                </div>
                <div className="flex justify-end pt-4">
                  <Button onClick={handleNotificationUpdate} disabled={loading}>
                    <Save className="h-4 w-4 mr-2" />
                    {t('profile.savePreferences', 'Save Preferences')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProfilePage;
